import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

interface PrivacyPolicyProps {
  onNavigate?: (route: string) => void;
}

export function PrivacyPolicy({ onNavigate }: PrivacyPolicyProps) {
  return (
    <div className="min-h-screen bg-background py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto bg-card p-8 rounded-lg shadow border">
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => window.history.back()}
            style={{ paddingLeft: "0" }}
            className="text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        </div>
        <div className="prose prose-slate dark:prose-invert max-w-none">
          <p className="text-sm text-muted-foreground mb-1"><strong>Source Code Control Limited</strong></p>
          <p className="text-sm text-muted-foreground mb-4"><em>ORbiT8 - Cloud Software Supply Chain Scanning</em></p>
          <h1 className="text-3xl font-bold text-foreground mb-2">Privacy Policy</h1>
          <h2 className="text-xl font-semibold text-foreground mb-4">How ORbiT8 Collects, Uses, and Protects Your Data</h2>
          <p className="text-sm text-muted-foreground mb-6">
            Effective Date: April 2026 | Version 1.0
          </p>

          <div className="overflow-x-auto mb-6">
            <table className="w-full text-sm border border-border">
              <tbody>
                <tr className="border-b border-border">
                  <td className="px-4 py-2 font-bold bg-muted">Company</td>
                  <td className="px-4 py-2">Source Code Control Limited</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="px-4 py-2 font-bold bg-muted">Product</td>
                  <td className="px-4 py-2">ORbiT8</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="px-4 py-2 font-bold bg-muted">Website</td>
                  <td className="px-4 py-2">
                    <a href="https://www.orbit8.co" className="text-primary hover:underline">orbit8.co</a>; <a href="https://sourcecodecontrol.com" className="text-primary hover:underline">sourcecodecontrol.com</a>
                  </td>
                </tr>
                <tr className="border-b border-border">
                  <td className="px-4 py-2 font-bold bg-muted">Contact</td>
                  <td className="px-4 py-2">
                    <a href="mailto:open@sourcecodecontrol.com" className="text-primary hover:underline">open@sourcecodecontrol.com</a>
                  </td>
                </tr>
                <tr>
                  <td className="px-4 py-2 font-bold bg-muted">Registered Office</td>
                  <td className="px-4 py-2">153 Mortimer Street, Herne Bay, Kent, CT6 5HA, United Kingdom</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="bg-muted border border-border rounded p-4 mb-8">
            <p className="text-sm italic">
              <strong>IMPORTANT:</strong> Please read this document carefully before using ORbiT8. By accessing or using the platform, you agree to be bound by these terms.
            </p>
          </div>

          <div className="space-y-6 text-foreground/80">

            <hr className="my-8 border-border" />

            <section id="introduction">
              <h2 className="text-2xl font-semibold text-foreground mb-4">1. Introduction</h2>
              <p>
                Source Code Control Limited ("we", "us", "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, store, and protect personal data when you use the ORbiT8 platform ("Platform") available at <a href="https://www.orbit8.co" className="text-primary hover:underline">https://www.orbit8.co</a>.
              </p>
              <p className="mt-3">
                This Policy should be read alongside our Terms and Conditions and, where applicable, our GDPR Policy. If you have any questions, please contact us at <a href="mailto:open@sourcecodecontrol.co" className="text-primary hover:underline">open@sourcecodecontrol.co</a>.
              </p>
              <p className="mt-3">
                We are the data controller in respect of personal data collected through the Platform. We are registered with the Information Commissioner's Office (ICO) in the United Kingdom.
              </p>
            </section>

            <hr className="my-8 border-border" />

            <section id="data-collection">
              <h2 className="text-2xl font-semibold text-foreground mb-4">2. What Personal Data We Collect</h2>

              <h3 className="text-lg font-semibold text-foreground mt-4 mb-2">2.1 Account and Registration Data</h3>
              <p>When you create an Account or sign up for the Platform, we collect:</p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>Full name</li>
                <li>Email address</li>
                <li>Organisation name and job title</li>
                <li>Username and password (stored in encrypted form)</li>
                <li>Country or region</li>
              </ul>

              <h3 className="text-lg font-semibold text-foreground mt-4 mb-2">2.2 Platform Usage Data</h3>
              <p>When you use the Platform, we automatically collect:</p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>Log data including IP address, browser type and version, operating system, pages visited, and time and date of access.</li>
                <li>Session data and feature usage patterns to understand how the Platform is used.</li>
                <li>Error and diagnostic data to support Platform stability and improvement.</li>
              </ul>

              <h3 className="text-lg font-semibold text-foreground mt-4 mb-2">2.3 Communication Data</h3>
              <p>We collect data when you contact us, including:</p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>Email correspondence and support requests.</li>
                <li>Survey responses and feedback you provide about the Platform.</li>
              </ul>

              <h3 className="text-lg font-semibold text-foreground mt-4 mb-2">2.4 Payment Data</h3>
              <p>
                Where paid subscriptions are available, payment data (such as billing address and card details) will be handled by a third-party payment processor. We do not store full payment card details.
              </p>

              <h3 className="text-lg font-semibold text-foreground mt-4 mb-2">2.5 Content Data</h3>
              <p>
                When you use the Platform to scan software, we process the code, files, repository metadata, and related materials you upload ("Content"). Where Content contains personal data, you are responsible as a data controller for ensuring lawful processing. We act as a data processor in respect of any personal data within your Content.
              </p>
            </section>

            <hr className="my-8 border-border" />

            <section id="usage">
              <h2 className="text-2xl font-semibold text-foreground mb-4">3. How We Use Your Data</h2>
              <p>We use your personal data for the following purposes:</p>

              <h3 className="text-lg font-semibold text-foreground mt-4 mb-2">3.1 Providing the Platform</h3>
              <ul className="list-disc pl-6 space-y-1">
                <li>Creating and managing your Account.</li>
                <li>Processing and delivering Scan Results.</li>
                <li>Communicating with you about your Account and use of the Platform.</li>
                <li>Providing technical support.</li>
              </ul>
              <p className="mt-2 text-sm">Legal basis: Performance of a contract (Article 6(1)(b) UK GDPR).</p>

              <h3 className="text-lg font-semibold text-foreground mt-4 mb-2">3.2 Improving the Platform</h3>
              <ul className="list-disc pl-6 space-y-1">
                <li>Analysing usage patterns to improve features and user experience.</li>
                <li>Developing new functionality and fixing issues.</li>
                <li>Conducting internal research and analytics.</li>
              </ul>
              <p className="mt-2 text-sm">Legal basis: Legitimate interests (Article 6(1)(f) UK GDPR) - to improve and develop our services.</p>

              <h3 className="text-lg font-semibold text-foreground mt-4 mb-2">3.3 Legal and Compliance Obligations</h3>
              <ul className="list-disc pl-6 space-y-1">
                <li>Complying with legal and regulatory requirements.</li>
                <li>Responding to lawful requests from public authorities.</li>
                <li>Enforcing our Terms and Conditions.</li>
              </ul>
              <p className="mt-2 text-sm">Legal basis: Legal obligation (Article 6(1)(c) UK GDPR) or legitimate interests.</p>

              <h3 className="text-lg font-semibold text-foreground mt-4 mb-2">3.4 Communications and Marketing</h3>
              <ul className="list-disc pl-6 space-y-1">
                <li>Sending product updates, security notices, and service announcements relevant to the Platform.</li>
                <li>Where you have consented, sending marketing communications about Source Code Control Limited products and services.</li>
              </ul>
              <p className="mt-2 text-sm">Legal basis: Consent (Article 6(1)(a) UK GDPR) for marketing; legitimate interests for service communications.</p>
            </section>

            <hr className="my-8 border-border" />

            <section id="retention">
              <h2 className="text-2xl font-semibold text-foreground mb-4">4. How Long We Keep Your Data</h2>
              <p>We retain personal data only for as long as necessary for the purposes described in this Policy, or as required by law.</p>
              <ul className="list-disc pl-6 space-y-1 mt-3">
                <li>Account data: Retained for the duration of your Account and for up to 12 months after closure.</li>
                <li>Usage and log data: Retained for up to 12 months.</li>
                <li>Content and Scan Results: Retained for the duration of your active use. We will delete Content on request or within 30 days of Account closure.</li>
                <li>Communication data: Retained for up to 3 years.</li>
                <li>Legal and financial records: Retained as required by applicable law (typically up to 7 years).</li>
              </ul>
            </section>

            <hr className="my-8 border-border" />

            <section id="sharing">
              <h2 className="text-2xl font-semibold text-foreground mb-4">5. Sharing Your Data</h2>
              <p>We do not sell your personal data. We may share your data in the following circumstances:</p>

              <h3 className="text-lg font-semibold text-foreground mt-4 mb-2">5.1 Service Providers</h3>
              <p>
                We use trusted third-party providers to support the operation of the Platform, including cloud hosting, email delivery, and analytics. These providers act as data processors on our behalf and are bound by appropriate data processing agreements.
              </p>

              <h3 className="text-lg font-semibold text-foreground mt-4 mb-2">5.2 Legal Requirements</h3>
              <p>
                We may disclose your data where required by law, court order, or to respond to lawful requests from regulatory or law enforcement authorities.
              </p>

              <h3 className="text-lg font-semibold text-foreground mt-4 mb-2">5.3 Business Transfers</h3>
              <p>
                In the event of a merger, acquisition, or sale of all or part of our business, your data may be transferred to the acquiring entity, subject to equivalent privacy protections.
              </p>

              <h3 className="text-lg font-semibold text-foreground mt-4 mb-2">5.4 With Your Consent</h3>
              <p>We will share your data with third parties in any other circumstance only with your explicit consent.</p>
            </section>

            <hr className="my-8 border-border" />

            <section id="transfers">
              <h2 className="text-2xl font-semibold text-foreground mb-4">6. International Data Transfers</h2>
              <p>
                Your data is primarily stored and processed within the United Kingdom and the European Economic Area (EEA). Where we transfer data outside these regions, we ensure appropriate safeguards are in place, such as Standard Contractual Clauses approved by the UK Information Commissioner's Office.
              </p>
            </section>

            <hr className="my-8 border-border" />

            <section id="cookies">
              <h2 className="text-2xl font-semibold text-foreground mb-4">7. Cookies and Tracking</h2>
              <p>The Platform uses cookies and similar tracking technologies to operate and improve the service. These include:</p>
              <ul className="list-disc pl-6 space-y-1 mt-3">
                <li><strong>Essential cookies:</strong> Required for the Platform to function, such as session management and authentication. These cannot be disabled.</li>
                <li><strong>Analytics cookies:</strong> Used to understand how users interact with the Platform, helping us improve functionality. These are only set with your consent.</li>
                <li><strong>Preference cookies:</strong> Used to remember your settings and preferences.</li>
              </ul>
              <p className="mt-3">You can manage your cookie preferences through your browser settings. Disabling certain cookies may affect the functionality of the Platform.</p>
            </section>

            <hr className="my-8 border-border" />

            <section id="rights">
              <h2 className="text-2xl font-semibold text-foreground mb-4">8. Your Rights</h2>
              <p>Under UK GDPR and the Data Protection Act 2018, you have the following rights in relation to your personal data:</p>
              <ul className="list-disc pl-6 space-y-1 mt-3">
                <li>Right of access - to request a copy of the personal data we hold about you.</li>
                <li>Right to rectification - to request correction of inaccurate or incomplete data.</li>
                <li>Right to erasure - to request deletion of your personal data, subject to legal obligations.</li>
                <li>Right to restriction - to request that we restrict processing of your data in certain circumstances.</li>
                <li>Right to data portability - to receive your data in a structured, machine-readable format.</li>
                <li>Right to object - to object to processing based on legitimate interests or for direct marketing.</li>
                <li>Rights related to automated decision-making - to request human review of any automated decisions that affect you.</li>
              </ul>
              <p className="mt-3">
                To exercise any of these rights, please contact us at <a href="mailto:open@sourcecodecontrol.co" className="text-primary hover:underline">open@sourcecodecontrol.co</a>. We will respond within one month. You also have the right to lodge a complaint with the Information Commissioner's Office (ICO) at <a href="https://www.ico.org.uk" className="text-primary hover:underline">www.ico.org.uk</a>.
              </p>
            </section>

            <hr className="my-8 border-border" />

            <section id="security">
              <h2 className="text-2xl font-semibold text-foreground mb-4">9. Security</h2>
              <p>
                We implement appropriate technical and organisational measures to protect your personal data against unauthorised access, loss, destruction, or alteration. These measures include encryption in transit and at rest, access controls, and regular security reviews.
              </p>
              <p className="mt-3">
                No system is completely secure. In the event of a personal data breach that is likely to result in risk to your rights and freedoms, we will notify you and the ICO as required by applicable law.
              </p>
            </section>

            <hr className="my-8 border-border" />

            <section id="third-party">
              <h2 className="text-2xl font-semibold text-foreground mb-4">10. Third-Party Links</h2>
              <p>
                The Platform may contain links to third-party websites or services. We are not responsible for the privacy practices of those third parties and encourage you to review their privacy policies independently.
              </p>
            </section>

            <hr className="my-8 border-border" />

            <section id="children">
              <h2 className="text-2xl font-semibold text-foreground mb-4">11. Children's Privacy</h2>
              <p>
                The Platform is not directed at or intended for use by individuals under the age of 18. We do not knowingly collect personal data from minors. If you believe we have inadvertently collected data from a minor, please contact us and we will delete it promptly.
              </p>
            </section>

            <hr className="my-8 border-border" />

            <section id="changes">
              <h2 className="text-2xl font-semibold text-foreground mb-4">12. Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. We will notify you of material changes by email or via the Platform. The updated Policy will be effective from the date indicated at the top of the document.
              </p>
            </section>

            <hr className="my-8 border-border" />

            <section id="contact">
              <h2 className="text-2xl font-semibold text-foreground mb-4">13. Contact</h2>
              <p>If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:</p>
              <div className="overflow-x-auto mt-4">
                <table className="w-full text-sm border border-border">
                  <tbody>
                    <tr className="border-b border-border">
                      <td className="px-4 py-2 font-bold bg-muted">Email</td>
                      <td className="px-4 py-2">
                        <a href="mailto:open@sourcecodecontrol.co" className="text-primary hover:underline">open@sourcecodecontrol.co</a>
                      </td>
                    </tr>
                    <tr className="border-b border-border">
                      <td className="px-4 py-2 font-bold bg-muted">Post</td>
                      <td className="px-4 py-2">Source Code Control Limited, Reading, Berkshire, United Kingdom</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-2 font-bold bg-muted">Website</td>
                      <td className="px-4 py-2">
                        <a href="https://www.orbit8.co" className="text-primary hover:underline">orbit8.co</a>; <a href="https://sourcecodecontrol.com" className="text-primary hover:underline">sourcecodecontrol.com</a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

          </div>
        </div>
      </div>
    </div>
  );
}
